import {
  PnoeLifestyleAnswers,
  PnoeLifestyleParameter,
  PnoeParameterOption,
} from "../../entities/pnoeMeasurement";
import { titleCaseOption } from "./pnoeFieldFormatting";

/**
 * The lifestyle questions a measurement carries (`participant.lifestyle_answers`,
 * a snapshot taken at test time) and the set the service currently asks
 * (`configuration.lifestyle_parameters`), reconciled for display and editing.
 *
 * Two sources because they can disagree: a question may have been reworded or
 * retired since the test, and a historical measurement may have no snapshot at
 * all. The snapshot is authoritative for *what was answered*; the configuration
 * is authoritative for *what can be answered now*.
 */

/** One displayable lifestyle question. */
export interface PnoeLifestyleRow {
  /** The question's `field_name`. */
  key: string;
  question: string;
  /** Help text; "" when there is none. */
  details: string;
  /** From the snapshot; null when the measurement has no answer. */
  value: string | null;
  label: string | null;
  /**
   * False for a snapshot key the configuration no longer lists. Such a row is
   * shown (the answer was given) but not offered for editing (the service
   * accepts only current, enabled choices).
   */
  configured: boolean;
}

/** Stable order for the configured questions: `sort_order`, then name. */
export const sortLifestyleParameters = (
  parameters: PnoeLifestyleParameter[] | null | undefined
): PnoeLifestyleParameter[] =>
  Array.isArray(parameters)
    ? [...parameters].sort(
        (a, b) =>
          (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
          a.field_name.localeCompare(b.field_name)
      )
    : [];

/**
 * Configured questions first, in `sort_order`, wording from the configuration
 * and the answer from the snapshot; then any snapshot entries the configuration
 * no longer lists, in the snapshot's own order, worded by the snapshot. Empty
 * on both sides → `[]`, and the section is not rendered at all.
 *
 * The snapshot's `label` is kept even for a configured question: it is what the
 * client was shown and chose, which the current wording may no longer match.
 */
export const buildLifestyleRows = (
  answers: PnoeLifestyleAnswers | null | undefined,
  parameters: PnoeLifestyleParameter[] | null | undefined
): PnoeLifestyleRow[] => {
  const snapshot = answers && typeof answers === "object" ? answers : {};
  const configured = sortLifestyleParameters(parameters);
  const configuredKeys = new Set(configured.map((p) => p.field_name));

  const rows: PnoeLifestyleRow[] = configured.map((parameter) => {
    const answer = snapshot[parameter.field_name];
    return {
      key: parameter.field_name,
      question: parameter.question,
      details: parameter.details ?? "",
      value: answer?.value ?? null,
      label: answer?.label ?? null,
      configured: true,
    };
  });

  Object.entries(snapshot).forEach(([key, answer]) => {
    if (configuredKeys.has(key) || !answer) return;
    rows.push({
      key,
      question: answer.question || titleCaseOption(key),
      details: answer.details ?? "",
      value: answer.value ?? null,
      label: answer.label ?? null,
      configured: false,
    });
  });

  return rows;
};

/** What `CustomSelect` takes: a title, an optional second line, and the value. */
export interface PnoeSelectOption {
  title: string;
  subtitle?: string;
  value: string;
}

/** The second line under a choice that the service no longer offers. */
export const DISABLED_OPTION_NOTE = "No longer offered";

/**
 * Options for an edit select: the enabled choices, plus the current value when
 * it is no longer among them.
 *
 * A stored answer can point at a choice that has since been disabled. Dropping
 * it would make the select show the raw value (`CustomSelect` falls back to
 * the value when no option matches) and read as "nothing selected" — so it is
 * kept, flagged, and the user can only move away from it.
 */
export const selectOptionsWithCurrent = (
  answers: PnoeParameterOption[] | null | undefined,
  current: string | null | undefined,
  currentLabel?: string | null
): PnoeSelectOption[] => {
  const all = Array.isArray(answers) ? answers : [];
  const options: PnoeSelectOption[] = all
    .filter((answer) => answer.is_enabled !== false)
    .map((answer) => ({ title: answer.label, value: answer.value }));

  if (current && !options.some((option) => option.value === current)) {
    const known = all.find((answer) => answer.value === current)?.label;
    options.push({
      title: currentLabel || known || titleCaseOption(current),
      subtitle: DISABLED_OPTION_NOTE,
      value: current,
    });
  }
  return options;
};

/**
 * The edit panel's draft is a flat map of field → value, as the PATCH bodies
 * are. Lifestyle answers are nested (`lifestyle_answers: { key: value }`), so
 * in the draft they live under a namespaced key and are folded back on Save.
 * Field names never contain a dot, so the prefix cannot collide.
 */
export const LIFESTYLE_DRAFT_PREFIX = "lifestyle.";

export const lifestyleDraftKey = (fieldName: string) =>
  `${LIFESTYLE_DRAFT_PREFIX}${fieldName}`;

export const isLifestyleDraftKey = (key: string) =>
  key.startsWith(LIFESTYLE_DRAFT_PREFIX);

/** The flat draft the edit panel keeps. */
export type PnoeEditDraft = Record<string, string | number | null>;

/** A PATCH body: the draft's scalars, plus the folded lifestyle map. */
export type PnoeEditBody = Record<
  string,
  string | number | null | Record<string, string>
>;

/**
 * Lifts the `lifestyle.<key>` entries of a changed-fields map into one
 * `lifestyle_answers` map; every other key passes through untouched.
 *
 * `lifestyle_answers` is only present when at least one answer changed — an
 * empty map would still be a "change" to the service. A null answer is dropped:
 * the service takes a choice value, and the select cannot clear one anyway.
 */
export const foldLifestyleDraft = (changed: PnoeEditDraft): PnoeEditBody => {
  const body: PnoeEditBody = {};
  const lifestyle: Record<string, string> = {};

  Object.entries(changed).forEach(([key, value]) => {
    if (!isLifestyleDraftKey(key)) {
      body[key] = value;
      return;
    }
    if (value === null || value === undefined || value === "") return;
    lifestyle[key.slice(LIFESTYLE_DRAFT_PREFIX.length)] = String(value);
  });

  if (Object.keys(lifestyle).length) body.lifestyle_answers = lifestyle;
  return body;
};
